<script setup lang="ts">
/**
 * `/news/:slug` — the one article this route is currently on. Replaces
 * `LandingScreen.vue`'s old `NewsOverlay` (a modal opened over the page);
 * as a real route the article deserves a real page rather than a dialog
 * with nothing behind it to dismiss back to.
 *
 * **The body is rendered as text, never as markup.** It is admin-authored
 * and lands in the page, the same position the legal documents and the meta
 * tags are in — see `LegalOverlay`'s own note. `white-space: pre-wrap` keeps
 * the author's paragraphs without handing an admin account a script tag on
 * a public page.
 */
import { ref, watch } from 'vue';

import SiteHeader from '../landing/SiteHeader.vue';
import SiteFooter from '../landing/SiteFooter.vue';
import { leaveNewsRoute, navigateNews } from '../state/news-route';
import { fetchNewsArticle, newsDisplayDate, type NewsArticle } from '../landing/site-api';
import { useScrolledHeader } from '../landing/scrolled-header';

const props = defineProps<{ slug: string }>();
const emit = defineEmits<{ play: [] }>();

/** See `NewsListPage`'s identical note — no hero here either. */
const sentinel = ref<HTMLElement | null>(null);
const { scrolled } = useScrolledHeader(sentinel);

/** See `NewsListPage`'s identical note — `SiteHeader` only ever asks for this. */
function onPlay(): void {
  leaveNewsRoute();
  emit('play');
}

const article = ref<NewsArticle | null>(null);
const state = ref<'loading' | 'ready' | 'missing'>('loading');

watch(
  () => props.slug,
  async (slug) => {
    article.value = null;
    state.value = 'loading';
    const loaded = await fetchNewsArticle(slug);
    // The visitor may have navigated on while the request was in flight.
    if (props.slug !== slug) return;
    article.value = loaded;
    state.value = loaded ? 'ready' : 'missing';
  },
  { immediate: true },
);
</script>

<template>
  <div class="landing news-page" data-test="news-article">
    <SiteHeader :scrolled="scrolled" @play="onPlay" />
    <div ref="sentinel" class="scroll-sentinel" aria-hidden="true"></div>

    <main>
      <section class="band">
        <div class="wrap wrap-narrow">
          <button type="button" class="btn btn-quiet btn-sm news-article-back" @click="navigateNews('/news')">
            ← Back to news
          </button>

          <p v-if="state === 'loading'" class="news-note">Fetching the article…</p>
          <p v-else-if="state === 'missing'" class="news-note">
            This article isn't public yet. It may still be scheduled, or it has been taken down.
          </p>
          <article v-else-if="article" class="news-article">
            <div v-if="article.imageUrl" class="news-article-art">
              <img :src="article.imageUrl" :alt="article.title" loading="lazy" decoding="async" />
            </div>
            <p class="news-article-date">{{ newsDisplayDate(article) }}</p>
            <h1 class="news-article-title">{{ article.title }}</h1>
            <p class="news-article-summary">{{ article.summary }}</p>
            <div class="news-article-body">{{ article.content }}</div>
          </article>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>
